/**
 * Application Logger Utility
 * 
 * Provides consistent logging across the application with environment-aware behavior.
 * In development: Logs to console with formatted output
 * In production: Can be extended to send to error tracking service (Sentry, LogRocket, etc.)
 * 
 * @module utils/logger
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Log levels with corresponding console methods and styles
 */
const LOG_LEVELS = {
  DEBUG: { method: 'log', color: '#6b7280', prefix: '🔍 DEBUG' },
  INFO: { method: 'info', color: '#3b82f6', prefix: 'ℹ️ INFO' },
  WARN: { method: 'warn', color: '#f59e0b', prefix: '⚠️ WARN' },
  ERROR: { method: 'error', color: '#ef4444', prefix: '❌ ERROR' },
};

/**
 * Format log message with timestamp and context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 * @returns {Array} Formatted console arguments
 */
function formatLogMessage(level, message, data) {
  const timestamp = new Date().toISOString();
  const { color, prefix } = LOG_LEVELS[level];
  
  const baseStyle = `color: ${color}; font-weight: bold;`;
  const resetStyle = 'color: inherit; font-weight: normal;';
  
  if (data !== undefined) {
    return [
      `%c${prefix}%c [${timestamp}] ${message}`,
      baseStyle,
      resetStyle,
      data
    ];
  }
  
  return [
    `%c${prefix}%c [${timestamp}] ${message}`,
    baseStyle,
    resetStyle
  ];
}

/**
 * Logger object with methods for different log levels
 */
const logger = {
  /**
   * Debug level logging - only in development
   * @param {string} message - Debug message
   * @param {*} [data] - Optional data to log
   */
  debug: (message, data) => {
    if (isDevelopment) {
      const args = formatLogMessage('DEBUG', message, data);
      console.log(...args);
    }
  },

  /**
   * Info level logging - only in development
   * @param {string} message - Info message
   * @param {*} [data] - Optional data to log
   */
  info: (message, data) => {
    if (isDevelopment) {
      const args = formatLogMessage('INFO', message, data);
      console.info(...args);
    }
  },

  /**
   * Warning level logging - always logged
   * @param {string} message - Warning message
   * @param {*} [data] - Optional data to log
   */
  warn: (message, data) => {
    const args = formatLogMessage('WARN', message, data);
    console.warn(...args);
    
    // In production, could send to error tracking
    // if (!isDevelopment) {
    //   sendToErrorTracking('warning', message, data);
    // }
  },

  /**
   * Error level logging - always logged
   * @param {string} message - Error message
   * @param {Error|*} [error] - Error object or data
   */
  error: (message, error) => {
    const args = formatLogMessage('ERROR', message, error);
    console.error(...args);
    
    // In production, send to error tracking service
    // if (!isDevelopment) {
    //   sendToErrorTracking('error', message, error);
    // }
  },

  /**
   * Log API request for debugging
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} [data] - Request data
   */
  apiRequest: (method, url, data) => {
    if (isDevelopment) {
      console.groupCollapsed(`🌐 API ${method} ${url}`);
      if (data) console.log('Request Data:', data);
      console.groupEnd();
    }
  },

  /**
   * Log API response for debugging
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} status - Response status
   * @param {*} [data] - Response data
   */
  apiResponse: (method, url, status, data) => {
    if (isDevelopment) {
      const isError = status >= 400;
      const icon = isError ? '❌' : '✅';
      console.groupCollapsed(`${icon} API ${method} ${url} - ${status}`);
      if (data) console.log('Response:', data);
      console.groupEnd();
    }
  },

  /**
   * Log component render for performance debugging
   * @param {string} componentName - Name of the component
   * @param {Object} [props] - Component props
   */
  render: (componentName, props) => {
    if (isDevelopment && process.env.REACT_APP_DEBUG_RENDERS === 'true') {
      console.log(`🔄 Render: ${componentName}`, props);
    }
  },

  /**
   * Log performance timing
   * @param {string} label - Operation label
   * @param {number} startTime - Start timestamp from performance.now()
   */
  performance: (label, startTime) => {
    if (isDevelopment) {
      const duration = performance.now() - startTime;
      const color = duration > 100 ? '#ef4444' : duration > 50 ? '#f59e0b' : '#10b981';
      console.log(
        `%c⏱️ ${label}: ${duration.toFixed(2)}ms`,
        `color: ${color}; font-weight: bold;`
      );
    }
  },

  /**
   * Create a named logger instance with prefix
   * @param {string} namespace - Logger namespace/prefix
   * @returns {Object} Namespaced logger
   */
  namespace: (namespace) => ({
    debug: (msg, data) => logger.debug(`[${namespace}] ${msg}`, data),
    info: (msg, data) => logger.info(`[${namespace}] ${msg}`, data),
    warn: (msg, data) => logger.warn(`[${namespace}] ${msg}`, data),
    error: (msg, error) => logger.error(`[${namespace}] ${msg}`, error),
  }),
};

export default logger;

// Named exports for convenience
export const { debug, info, warn, error, apiRequest, apiResponse } = logger;
